/**
 * ZPL template for the handover label (CROCS-970), design v5.
 *
 * Printed on a Zebra ZD421d (203 dpi, direct thermal, dispenser) on
 * 102 x 50 mm labels (812 x 400 dots), everything in landscape:
 *
 *   +-------------------+  ||  TrunkrsNr: ...9308  [PRIO]
 *   | TOUR              |  ||                      BOX 2/3
 *   | ----------------- |  ||  NEERZETTEN OK: <customer text>
 *   |        3          |  ||  --------------------------
 *   +-------------------+  ||  <driver remark>
 *   -------------------    ||
 *   STOP  41               ||             Datum: 27-07-2026
 *   -------------------    ||
 *
 * The tour box and stop row (the loading keys) match the partner example
 * label. Deliberately NO driver name: personal data stays off parcels.
 */

const LABEL_WIDTH = 812
const LABEL_LENGTH = 400

// left block: boxed tour + stop row
const BLOCK_X = 24
const BLOCK_WIDTH = 356

// right column: header, flags, sections, date
const RIGHT_X = 404
const RIGHT_WIDTH = LABEL_WIDTH - RIGHT_X - 24

const SECTION_FONT = 26
const SECTION_LINE_HEIGHT = 30
const SECTION_MAX_LINES = 3
// font 26 fits roughly 28 characters on the 384-dots right column
export const SECTION_CHARS_PER_LINE = 28
export const SECTION_MAX_CHARS = SECTION_CHARS_PER_LINE * SECTION_MAX_LINES

// font 0 characters are roughly 0.5x their height wide
const CHAR_WIDTH_RATIO = 0.5

export interface HandoverLabelData {
  tourNumber: number | string
  stopNumber: number | string
  /** parcel number (TrunkrsNr); the label shows the last four characters */
  parcelId?: string | null
  /** delivery date, preformatted (dd-mm-yyyy) */
  date?: string | null
  /** day-network delivery (rare); night is the default and gets no marker */
  isDayDelivery?: boolean
  /** shipment is flagged priority (must-deliver) */
  isPriority?: boolean
  /** recipient gave permission to leave the parcel unattended */
  leaveBehindPermission?: boolean
  /** where to leave the parcel, provided by the recipient */
  leaveBehindRemark?: string | null
  /** parcel x of y going to the same stop */
  multicolli?: { index: number; total: number } | null
  remark?: string | null
  /**
   * When set, an exception label is rendered instead of the normal one: a
   * scanned parcel that has no tour in this handover (cancelled, return,
   * mis-sorted, unknown, ...). Guarantees one label per scan so nothing
   * shifts on the belt. The reason is shown in a bold inverse banner.
   */
  exception?: { reason: string; barcode?: string | null } | null
}

/**
 * Maps a shipment state to the short Dutch reason printed on an exception
 * label. Any state we don't specifically recognise means "the parcel exists
 * but has no tour in this handover" -> GEEN RIT. The caller decides between
 * GEEN RIT and NIET GEVONDEN when no state could be resolved at all.
 */
export const classifyHandoverException = (state?: string | null): string => {
  const s = String(state ?? '').toUpperCase()
  if (s.includes('CANCELLED')) return 'GEANNULEERD'
  if (s.includes('RETURN') || s.includes('RETOUR')) return 'RETOUR'
  if (s.includes('SORTED') && s.includes('MIS')) return 'VERKEERD GESORTEERD'
  if (s.includes('DESTROY')) return 'VERNIETIGEN'
  return 'GEEN RIT'
}

/**
 * ^FD field data must not contain ZPL control characters. Newlines (a remark
 * can contain appended parts) are flattened; anything non-printable is
 * dropped so a weird remark can never break the label.
 */
export const sanitizeForZpl = (value: string): string =>
  Array.from(value.replace(/[\^~\\]/g, ' ').replace(/\s+/g, ' '))
    .filter((char) => {
      const code = char.charCodeAt(0)
      return code >= 32 && code !== 127
    })
    .join('')
    .trim()

/**
 * Word-wraps into at most maxLines lines of maxChars; the last line gets an
 * ellipsis when text is cut.
 */
export const wrapForLabel = (
  value: string,
  maxChars: number = SECTION_CHARS_PER_LINE,
  maxLines: number = SECTION_MAX_LINES,
): string[] => {
  const words = value.split(' ').filter(Boolean)
  const lines: string[] = []
  let current = ''

  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word
    if (candidate.length <= maxChars) {
      current = candidate
      return
    }
    if (current) {
      lines.push(current)
    }
    current = word.slice(0, maxChars)
  })
  if (current) {
    lines.push(current)
  }

  if (lines.length > maxLines) {
    const cut = lines.slice(0, maxLines)
    cut[maxLines - 1] = `${cut[maxLines - 1].slice(0, maxChars - 3)}...`
    return cut
  }
  return lines
}

const text = (x: number, y: number, size: number, content: string) => [
  `^FO${x},${y}`,
  `^A0N,${size},${size}`,
  `^FD${content}^FS`,
]
// white-on-black text: ^FR reverses the field over the black banner behind it
const reversedText = (x: number, y: number, size: number, content: string) => [
  `^FO${x},${y}`,
  `^A0N,${size},${size}`,
  '^FR',
  `^FD${content}^FS`,
]
const rect = (
  x: number,
  y: number,
  width: number,
  height: number,
  thickness: number,
  rounding = 0,
) => [`^FO${x},${y}`, `^GB${width},${height},${thickness},B,${rounding}^FS`]

// shrink the exception banner text so the longest reasons still fit one line
const exceptionReasonSize = (length: number): number => {
  if (length > 14) return 58
  if (length > 10) return 74
  return 96
}

// centers content of the given font size within a block
const centeredX = (
  value: string,
  size: number,
  blockX: number,
  blockWidth: number,
): number =>
  Math.max(
    blockX + 12,
    Math.round(
      blockX + (blockWidth - value.length * size * CHAR_WIDTH_RATIO) / 2,
    ),
  )

export const buildHandoverLabelZpl = (data: HandoverLabelData): string => {
  const tourNumber = sanitizeForZpl(String(data.tourNumber ?? ''))
  const stopNumber = sanitizeForZpl(String(data.stopNumber ?? ''))
  const parcelId = sanitizeForZpl(data.parcelId ?? '')
  const date = sanitizeForZpl(data.date ?? '')
  const multicolli =
    data.multicolli && data.multicolli.total > 1 ? data.multicolli : null

  const leaveBehindText = data.leaveBehindPermission
    ? sanitizeForZpl(
        data.leaveBehindRemark
          ? `NEERZETTEN OK: ${data.leaveBehindRemark}`
          : 'NEERZETTEN OK',
      )
    : ''
  const remark = sanitizeForZpl(data.remark ?? '')

  const lines: string[] = [
    '^XA',
    '^CI28', // UTF-8 field data (accents in remarks)
    `^PW${LABEL_WIDTH}`,
    `^LL${LABEL_LENGTH}`,
    '^MMP', // peel mode: dispenser holds until the label is taken
    '^LH0,0',
  ]

  // exception label: a scanned parcel with no tour in this handover. One label
  // per scan keeps the belt in sync; a bold inverse banner makes it impossible
  // to mistake for a delivery so the loader sets it aside.
  if (data.exception) {
    const reason = sanitizeForZpl(data.exception.reason || 'CONTROLEREN')
    const barcode = sanitizeForZpl(data.exception.barcode ?? '')
    const bannerH = 156
    // thick outer frame + solid black banner (thickness = height fills it)
    lines.push(...rect(6, 6, LABEL_WIDTH - 12, LABEL_LENGTH - 12, 6))
    lines.push(...rect(6, 6, LABEL_WIDTH - 12, bannerH, bannerH))
    // reason sized down for the longer words so it always fits on one line
    const reasonSize = exceptionReasonSize(reason.length)
    lines.push(
      ...reversedText(
        centeredX(reason, reasonSize, 6, LABEL_WIDTH - 12),
        Math.round(6 + (bannerH - reasonSize) / 2),
        reasonSize,
        reason,
      ),
    )
    if (parcelId) {
      lines.push(...text(40, 196, 54, `TrunkrsNr: ...${parcelId.slice(-4)}`))
    }
    if (barcode) {
      lines.push(...text(40, 262, 40, `Barcode: ${barcode}`))
    }
    lines.push(...text(40, 328, 34, 'Geen bezorging - apart leggen'))
    lines.push('^XZ')
    return lines.join('\n')
  }

  // left block, matching the partner example: boxed tour number with a
  // ruled caption, then the stop as caption + number between two rules
  lines.push(...rect(BLOCK_X, 16, BLOCK_WIDTH, 220, 5, 1))
  lines.push(...text(48, 32, 32, 'TOUR'))
  lines.push(...rect(48, 76, BLOCK_WIDTH - 96, 3, 3))
  lines.push(
    ...text(
      centeredX(tourNumber, 150, BLOCK_X, BLOCK_WIDTH),
      92,
      150,
      tourNumber,
    ),
  )

  lines.push(...rect(BLOCK_X, 252, BLOCK_WIDTH, 4, 4))
  lines.push(...text(36, 330, 36, 'STOP'))
  lines.push(...text(150, 268, 110, stopNumber))
  lines.push(...rect(BLOCK_X, 388, BLOCK_WIDTH, 4, 4))

  // right column header: parcel id left, priority frame with the multicolli
  // indicator below it in the top-right corner
  if (parcelId) {
    lines.push(
      ...text(RIGHT_X, 16, SECTION_FONT, `TrunkrsNr: ...${parcelId.slice(-4)}`),
    )
  }
  if (data.isPriority) {
    lines.push(...rect(648, 12, 140, 44, 3, 2))
    lines.push(...text(centeredX('PRIO', 30, 648, 140), 20, 30, 'PRIO'))
  }
  if (multicolli) {
    const multiText = `BOX ${multicolli.index}/${multicolli.total}`
    lines.push(
      ...text(
        centeredX(multiText, 28, 648, 140),
        data.isPriority ? 66 : 20,
        28,
        multiText,
      ),
    )
  }

  // sections: leave-behind first, then driver remark; a rule between them
  // only when both are present. On (rare) day deliveries the last section
  // gives up a line so the DAGLEVERING badge always fits below.
  let sectionY = 108
  const sections = [leaveBehindText, remark].filter(Boolean)
  sections.forEach((sectionText, index) => {
    if (index > 0) {
      lines.push(...rect(RIGHT_X, sectionY, RIGHT_WIDTH, 3, 3))
      sectionY += 14
    }
    const maxLines =
      data.isDayDelivery && index === sections.length - 1
        ? SECTION_MAX_LINES - 1
        : SECTION_MAX_LINES
    wrapForLabel(sectionText, SECTION_CHARS_PER_LINE, maxLines).forEach(
      (line) => {
        lines.push(...text(RIGHT_X, sectionY, SECTION_FONT, line))
        sectionY += SECTION_LINE_HEIGHT
      },
    )
    sectionY += 12
  })

  // day-delivery badge above the date (rare; night gets no marker)
  if (data.isDayDelivery) {
    lines.push(...rect(548, 300, 240, 48, 3, 2))
    lines.push(
      ...text(centeredX('DAGLEVERING', 30, 548, 240), 310, 30, 'DAGLEVERING'),
    )
  }
  if (date) {
    const dateText = `Datum: ${date}`
    const dateX = Math.round(
      LABEL_WIDTH - 24 - dateText.length * SECTION_FONT * CHAR_WIDTH_RATIO,
    )
    lines.push(...text(dateX, 360, SECTION_FONT, dateText))
  }

  lines.push('^XZ')
  return lines.join('\n')
}
